import { z } from "zod";

import { AtomIdSchema, CellIdSchema, JsonValueSchema, PositionIdSchema } from "./common.js";

export const BoardPhaseSchema = z.enum([
  "empty",
  "framing",
  "contested",
  "ready",
  "converged",
]);
export type BoardPhase = z.infer<typeof BoardPhaseSchema>;

export const AttentionVectorSchema = z.object({
  entropy: z.number().int().nonnegative(),
  forcingThreats: z.number().int().nonnegative(),
  lineDebt: z.number().int().nonnegative(),
  proofDebt: z.number().int().nonnegative(),
});
export type AttentionVector = z.infer<typeof AttentionVectorSchema>;

export const PositionEvaluationSchema = z.object({
  attentionVector: AttentionVectorSchema,
  phase: BoardPhaseSchema,
  summary: z.string(),
});
export type PositionEvaluation = z.infer<typeof PositionEvaluationSchema>;

export const ThreatViewSchema = z.object({
  cellId: CellIdSchema,
  sourceAtomId: AtomIdSchema,
  summary: z.string(),
  targetAtomId: AtomIdSchema,
});
export type ThreatView = z.infer<typeof ThreatViewSchema>;

export const ProofDebtViewSchema = z.object({
  summary: z.string(),
  targetAtomId: AtomIdSchema,
  threatAtomId: AtomIdSchema.optional(),
});
export type ProofDebtView = z.infer<typeof ProofDebtViewSchema>;

export const SuggestedActionSchema = z.object({
  command: z.array(JsonValueSchema),
  kind: z.string().min(1),
  previewPhase: BoardPhaseSchema.optional(),
  reason: z.string().min(1),
  score: z.number().optional(),
});
export type SuggestedAction = z.infer<typeof SuggestedActionSchema>;

export const PathCandidateViewSchema = z.object({
  pathId: AtomIdSchema,
  reasons: z.array(z.string()),
  score: z.number(),
  selected: z.boolean(),
  summary: z.string(),
});
export type PathCandidateView = z.infer<typeof PathCandidateViewSchema>;

export const SearchStatsSchema = z.object({
  attentionAtoms: z.number().int().nonnegative(),
  attentionCells: z.number().int().nonnegative(),
  exploredBranches: z.number().int().nonnegative(),
  searchedDepth: z.number().int().positive(),
  transpositionHits: z.number().int().nonnegative().optional(),
  visitedNodes: z.number().int().nonnegative().optional(),
});
export type SearchStats = z.infer<typeof SearchStatsSchema>;

export const SearchReportSchema = z.object({
  attentionVector: AttentionVectorSchema,
  candidateLines: z.array(PathCandidateViewSchema).optional(),
  mainline: z.array(AtomIdSchema),
  phase: BoardPhaseSchema,
  principalVariation: z.array(PositionIdSchema),
  proofs: z.array(ProofDebtViewSchema),
  responses: z.array(SuggestedActionSchema),
  stats: SearchStatsSchema,
  threats: z.array(ThreatViewSchema),
});
export type SearchReport = z.infer<typeof SearchReportSchema>;
